import { useState, useEffect, useMemo, useRef } from 'react';
import { Home, Briefcase, Star, Clock, Navigation, Plus, MapPin, Trash2, Search, Bus } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { useAuthStore } from '../../stores/authStore';
import { useVisibleRecentSearches } from '../../hooks/useVisibleRecentSearches';
import { quickCategories } from '../../constants/categories';
import { getCategories, Category } from '../../services/api';
import { DynamicIcon, colorClasses } from '../ui/DynamicIcon';
import type { Place } from '../../types';
import { MARKETING_URL } from '../../lib/env';
import { TransportNearMe } from '../transport/TransportNearMe';

interface HomeContentProps {
  onGetDirections?: () => void;
  onAddPlace?: (label: 'home' | 'work' | 'saved') => void;
  onAddMissingPlace?: () => void;
  onNavigateToStop?: (name: string, coords: { lat: number; lng: number }) => void;
}

export function HomeContent({
  onGetDirections,
  onAddPlace,
  onAddMissingPlace,
  onNavigateToStop,
}: HomeContentProps) {
  const { performSearch, setSearchQuery, selectPlace, setSelectedPlaceFromData } = useMapStore();
  const recentSearches = useVisibleRecentSearches();
  const transportSectionRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, savedPlaces, openAuthModal, requireAuth, removeSavedPlace } = useAuthStore();

  // Saved places drawer state
  const [showSavedDrawer, setShowSavedDrawer] = useState(false);
  const [savedFilter, setSavedFilter] = useState('');

  // API categories state
  const [apiCategories, setApiCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Fetch categories from API
  useEffect(() => {
    getCategories()
      .then((cats) => {
        setApiCategories(cats);
        setCategoriesLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load categories:', err);
        setCategoriesLoading(false);
      });
  }, []);

  // Use API categories if available, otherwise fallback to mock
  const displayCategories = apiCategories.length > 0
    ? apiCategories.slice(0, 6).map(cat => ({
      id: cat.id,
      icon: cat.icon,
      color: cat.color,
      label: cat.name,
    }))
    : quickCategories;

  const handleCategorySearch = (label: string) => {
    setSearchQuery(label);
    useMapStore.getState().setSheetState('collapsed');
    useMapStore.getState().setShowSearchResults(true);
    performSearch(label);
  };

  const handleRecentClick = (placeId: string | undefined) => {
    if (placeId) {
      selectPlace(placeId);
    }
  };

  // When clicking a saved place, set it as selectedPlace so a pin shows on the map
  const handleSavedPlaceClick = (place: typeof savedPlaces[0]) => {
    const placeData: Place = {
      id: place.placeId || `saved-${place.id}`,
      name: place.name,
      category: place.label === 'home' ? 'place' : place.label === 'work' ? 'place' : 'place',
      address: place.address,
      coordinates: place.coordinates,
    };
    setSelectedPlaceFromData(placeData);
  };

  // Navigate to a saved place via directions
  const handleSavedPlaceDirections = (e: React.MouseEvent, place: typeof savedPlaces[0]) => {
    e.stopPropagation();
    requireAuth(() => {
      const placeData: Place = {
        id: place.placeId || `saved-${place.id}`,
        name: place.name,
        category: 'place',
        address: place.address,
        coordinates: place.coordinates,
      };
      setSelectedPlaceFromData(placeData);
      if (onGetDirections) {
        // Small delay to let selectedPlace be set before directions panel reads it
        setTimeout(() => onGetDirections(), 50);
      }
    });
  };

  const handleAddSavedPlace = (label: 'home' | 'work' | 'saved') => {
    if (!isAuthenticated) {
      openAuthModal('login', () => {
        // After login, show add place UI
        onAddPlace?.(label);
      });
    } else {
      onAddPlace?.(label);
    }
  };

  const handleRecentDirections = (e: React.MouseEvent, placeId: string | undefined) => {
    e.stopPropagation();
    if (!placeId) return;

    requireAuth(() => {
      selectPlace(placeId);
      if (onGetDirections) {
        onGetDirections();
      }
    });
  };

  const handleDeleteSavedPlace = (e: React.MouseEvent, placeId: string) => {
    e.stopPropagation();
    removeSavedPlace(placeId);
  };

  // Get saved places by label
  const homePlace = savedPlaces.find(p => p.label === 'home');
  const workPlace = savedPlaces.find(p => p.label === 'work');
  const otherSavedPlaces = savedPlaces.filter(p => p.label === 'saved');

  // Filtered saved places for search
  const filteredSavedPlaces = useMemo(() => {
    if (!savedFilter.trim()) return otherSavedPlaces;
    const q = savedFilter.toLowerCase();
    return otherSavedPlaces.filter(p =>
      p.name.toLowerCase().includes(q) || p.address.toLowerCase().includes(q)
    );
  }, [otherSavedPlaces, savedFilter]);

  return (
    <div className="space-y-6">
      {/* Saved Places */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Your Places
        </h3>

        {/* Home, Work, Saved — compact icon row */}
        <div className="flex items-center gap-2 mb-3">
          {/* Home */}
          <button
            onClick={() => homePlace ? handleSavedPlaceClick(homePlace) : handleAddSavedPlace('home')}
            className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30"
            title={homePlace ? homePlace.name : 'Set Home'}
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-100 dark:bg-blue-900/40">
              <Home size={18} className="text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-xs font-medium truncate max-w-full px-1 text-blue-700 dark:text-blue-300">
              {homePlace ? 'Home' : 'Set Home'}
            </span>
          </button>

          {/* Work */}
          <button
            onClick={() => workPlace ? handleSavedPlaceClick(workPlace) : handleAddSavedPlace('work')}
            className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30"
            title={workPlace ? workPlace.name : 'Set Work'}
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center bg-orange-100 dark:bg-orange-900/40">
              <Briefcase size={18} className="text-orange-600 dark:text-orange-400" />
            </div>
            <span className="text-xs font-medium truncate max-w-full px-1 text-orange-700 dark:text-orange-300">
              {workPlace ? 'Work' : 'Set Work'}
            </span>
          </button>

          {/* Saved */}
          <button
            onClick={() => {
              if (otherSavedPlaces.length > 0) {
                setShowSavedDrawer(!showSavedDrawer);
                if (showSavedDrawer) setSavedFilter('');
              } else {
                handleAddSavedPlace('saved');
              }
            }}
            className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30"
            title="Saved Places"
          >
            <div className="relative">
              <div className="w-9 h-9 rounded-full flex items-center justify-center bg-purple-100 dark:bg-purple-900/40">
                <Star size={18} className="text-purple-600 dark:text-purple-400" />
              </div>
              {otherSavedPlaces.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 text-[9px] font-bold bg-purple-500 text-white rounded-full flex items-center justify-center">
                  {otherSavedPlaces.length}
                </span>
              )}
            </div>
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
              Saved
            </span>
          </button>
        </div>

        {/* Saved places drawer — expands below the icon row */}
        {showSavedDrawer && otherSavedPlaces.length > 0 && (
          <div className="mb-3">
            {otherSavedPlaces.length >= 5 && (
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={savedFilter}
                  onChange={(e) => setSavedFilter(e.target.value)}
                  placeholder="Filter saved places..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-400"
                />
              </div>
            )}

            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {filteredSavedPlaces.length === 0 && savedFilter.trim() ? (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-3">No places match "{savedFilter}"</p>
              ) : (
                filteredSavedPlaces.map((place) => (
                  <div
                    key={place.id}
                    className="flex items-center gap-2 group"
                  >
                    <button
                      onClick={() => handleSavedPlaceClick(place)}
                      className="flex-1 flex items-center gap-2.5 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-0 overflow-hidden"
                    >
                      <MapPin size={14} className="text-purple-500 dark:text-purple-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{place.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{place.address}</p>
                      </div>
                    </button>
                    <button
                      onClick={(e) => handleSavedPlaceDirections(e, place)}
                      className="p-1.5 text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex-shrink-0"
                      title="Get directions"
                    >
                      <Navigation size={14} />
                    </button>
                    <button
                      onClick={(e) => handleDeleteSavedPlace(e, place.id)}
                      className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>

            <button
              onClick={() => handleAddSavedPlace('saved')}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 mt-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors text-xs font-medium"
            >
              <Plus size={14} />
              Add place
            </button>
          </div>
        )}
      </div>

      <div ref={transportSectionRef}>
        <TransportNearMe onNavigateToStop={onNavigateToStop} />
      </div>

      {/* Quick Categories */}
      <div>
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Explore Nearby
        </h3>
        {categoriesLoading ? (
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="flex flex-col items-center p-4 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full mb-2" />
                <div className="w-12 h-3 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => handleCategorySearch('Transport')}
              className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center mb-2">
                <Bus size={20} className="text-white" />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300">Transport</span>
            </button>
            {displayCategories.map((cat) => {
              const colors = colorClasses[cat.color || 'gray'] || colorClasses.gray;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySearch(cat.label)}
                  className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-800 rounded-2xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center mb-2`}>
                    <DynamicIcon name={cat.icon} size={20} className="text-white" />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{cat.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Missing Place CTA */}
      {onAddMissingPlace && (
        <button
          data-tour-target="add-missing-place"
          type="button"
          onClick={onAddMissingPlace}
          className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-emerald-50 to-cyan-50 dark:from-emerald-900/20 dark:to-cyan-900/20 border border-emerald-200 dark:border-emerald-800 hover:from-emerald-100 hover:to-cyan-100 dark:hover:from-emerald-900/30 dark:hover:to-cyan-900/30 transition-all"
        >
          <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
            <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Can't find your place?</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Add it and get a postcode instantly</p>
          </div>
          <Plus size={18} className="text-emerald-500 flex-shrink-0" />
        </button>
      )}

      {/* Recent Activity */}
      {recentSearches.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Recent
          </h3>
          <div className="space-y-2">
            {recentSearches.slice(0, 3).map((recent) => (
              <div
                key={recent.id}
                className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors overflow-hidden"
              >
                <button
                  onClick={() => handleRecentClick(recent.result?.id)}
                  className="flex-1 flex items-center gap-3 p-3 text-left min-w-0"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-gray-500 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{recent.query}</p>
                  </div>
                </button>
                {/* Navigation button */}
                {recent.result && (
                  <button
                    onClick={(e) => handleRecentDirections(e, recent.result?.id)}
                    className="p-3 mr-2 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-full transition-colors"
                    title="Get directions"
                  >
                    <Navigation size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Copyright */}
      <div className="pt-2 pb-4 text-center">
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
          © {new Date().getFullYear()} <a href={MARKETING_URL} target="_blank" rel="noopener noreferrer" className="hover:text-primary-500 transition-colors">Coordal Technologies Ltd</a>. Reg No. CS110510526. All rights reserved.
        </p>
      </div>
    </div>
  );
}
